import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

export default function JobFilterForm({ 
  searchParams, 
  setParams,
  initialTypeOfWork = "",
  initialJobType = "",
  initialPostDate = ""
}) {
  const history = useHistory();
  const isInitialMount = useRef(true);
  const [worktypeArray, setWorktypeArray] = useState([]);
  const [jobtypeArray, setJobtypeArray] = useState([]);

  const [typeOfWork, setTypeOfWork] = useState(
    initialTypeOfWork ? (Array.isArray(initialTypeOfWork) ? initialTypeOfWork : initialTypeOfWork.split(",")) : []
  );
  const [jobType, setJobType] = useState(
    initialJobType ? (Array.isArray(initialJobType) ? initialJobType : initialJobType.split(",")) : []
  );
  const [postDate, setPostDate] = useState(initialPostDate);

  const postDateList = [
    "Expiring Soon",
    "Today",
    "Yesterday",  
    "Last 7 Days",
    "Last 14 Days",
    "Last 30 Days",
    "All",
  ];
  const [postDateArray, setPostDateArray] = useState(postDateList);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    
    axios.get(constvar.api_url + "home/job_filter_data", { signal: controller.signal }).then((response) => {
      if (!isMounted) return;
      if (response.data.status == "success") {
        setWorktypeArray(response.data.worktype);
        setJobtypeArray(response.data.jobtype);
      } else {
        setWorktypeArray([]);
        setJobtypeArray([]);
      }
    }).catch(error => {
      if (!isMounted) return;
      if (error.name !== "AbortError" && error.name !== "CanceledError") {
        console.error("Failed to load job filter data:", error);
      }
    });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // Sync form state when initial props change (e.g., URL changes, browser back button)
  useEffect(() => {
    if (isInitialMount.current) return;
    
    setTypeOfWork(initialTypeOfWork ? (Array.isArray(initialTypeOfWork) ? initialTypeOfWork : initialTypeOfWork.split(",")) : []);
    setJobType(initialJobType ? (Array.isArray(initialJobType) ? initialJobType : initialJobType.split(",")) : []);
    setPostDate(initialPostDate || "");
  }, [initialTypeOfWork, initialJobType, initialPostDate]);

  useEffect(() => {
    // Skip updating URL on initial mount, only update when user changes filters
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Update URL params only after initial mount
    const params = new URLSearchParams();
    
    // Maintain top search bar filters from searchParams
    if (searchParams.keyword) params.append("keyword", searchParams.keyword);
    if (searchParams.location) params.append("location", searchParams.location);
    if (searchParams.job_category) params.append("job_category", searchParams.job_category);
    if (searchParams.company) params.append("company", searchParams.company);

    if (typeOfWork && typeOfWork.length > 0) params.append("typeOfWork", typeOfWork.join(","));
    if (jobType && jobType.length > 0) params.append("jobType", jobType.join(","));
    if (postDate) params.append("postDate", postDate);
    
    history.push(`/job_search?${params.toString()}`);
  }, [typeOfWork, jobType, postDate]);

  const addWorkTypeOptions = (e) => {
    var id = e.currentTarget.value;
    var workType = [...typeOfWork];
    if (workType.includes(id)) {
      workType.splice(workType.indexOf(id), 1);
      setTypeOfWork(workType);
      return;
    }
    workType.push(id);
    setTypeOfWork(workType);
  };

  const addJobTypeOptions = (e) => {
    var id = e.currentTarget.value;
    var jobTypeArr = [...jobType];
    if (jobTypeArr.includes(id)) {
      jobTypeArr.splice(jobTypeArr.indexOf(id), 1);
      setJobType(jobTypeArr);
      return;
    }
    jobTypeArr.push(id);
    setJobType(jobTypeArr);
  };

  return (
    <form>
      <div className="widget tw-sidebar-tags-wrap">
        <h4 className="section-head-small mb-3">
          <i className="ri-list-check"></i> Filter
        </h4>
      </div>

      <div className="twm-sidebar-ele-filter">
        <h4 className="section-head-small mb-3">
          <i className="ri-briefcase-line"></i> Type of Work
        </h4>
        <ul>
          {worktypeArray.length > 0
            ? worktypeArray.map((wtype, ind) => (
                <li key={`work-type${ind}`}>
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input"
                      id={`workType${ind}`} value={wtype.id} name="typeOfWork[]"
                      checked={typeOfWork.includes(wtype.id.toString())}
                      onChange={addWorkTypeOptions} />
                    <label className="form-check-label" htmlFor={`workType${ind}`} >
                      {wtype.title}
                    </label>
                  </div>
                </li>
              ))
            : ""}
        </ul>
      </div>

      <div className="twm-sidebar-ele-filter">
        <h4 className="section-head-small mb-3">
          <i className="ri-briefcase-line"></i> Job Type
        </h4>
        <ul>
          {jobtypeArray.length > 0
            ? jobtypeArray.map((jtype, idx) => (
                <li key={`job-type${idx}`}>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`jobTypeCheck${idx}`}
                      value={jtype.id}
                      checked={jobType.includes(jtype.id.toString())}
                      onChange={addJobTypeOptions}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`jobTypeCheck${idx}`}
                    >
                      {jtype.title}
                    </label>
                  </div>
                  <span className="twm-job-type-count">
                    {jtype.job_type_count}
                  </span>
                </li>
              ))
            : ""}
        </ul>
      </div>

      <div className="twm-sidebar-ele-filter">
        <h4 className="section-head-small mb-3">
          <i className="ri-calendar-line"></i> Date Posts
        </h4>
        <ul>
          {postDateArray.length > 0
            ? postDateArray.map((title, ind) => (
                <li key={`post-data${ind}`}>
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="postdate"
                      id={title}
                      value={title}
                      checked={postDate === title}
                      onChange={(e) => {
                        setPostDate(e.currentTarget.value);
                      }}
                    />
                    <label className="form-check-label" htmlFor={title}>
                      {title}
                    </label>
                  </div>
                </li>
              ))
            : null}
        </ul>
      </div>
    </form>
  );
}
